// Orquestrador da rota /e. Fluxo: pareamento -> carrega o rascunho (GET) -> edição
// com autosave debounced (PATCH) -> "Copiar tudo" (flush do PATCH, copia e marca a
// nota copiada, congelando os diffs do piloto). "Encerrar e limpar" é só-cliente.
//
// O uuid da sessão é a credencial — guardado em sessionStorage para sobreviver a um
// refresh da aba, e apagado ao encerrar. Expira sozinho no servidor em 24 h.

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { carregarSoap, marcarCopiada, salvarSoap } from '../../api/client';
import { copiar } from '../../lib/clipboard';
import { CAMPOS, type CampoSoap, type Soap, type StatusSessao } from '../../api/types';
import { cor, fonte } from '../../styles/tokens';
import { Cabecalho } from './Cabecalho';
import { CampoSoap as CampoSoapView } from './CampoSoap';
import { CampoAvaliacao } from './CampoAvaliacao';
import { PainelLateral } from './PainelLateral';
import { Pareamento } from './Pareamento';

const CHAVE_SESSAO = 'vmed:sessionId';
const CHAVE_CODIGO = 'vmed:codigo';

const ROTULOS: Record<CampoSoap, string> = {
  subjetivo: 'SUBJETIVO',
  objetivo: 'OBJETIVO',
  avaliacao: 'AVALIAÇÃO',
  plano: 'PLANO',
};

// Texto de "Copiar tudo": os quatro campos rotulados. A nota de revisão tem botão
// próprio (como no mockup), então não entra aqui para não duplicar.
function textoCompleto(s: Soap): string {
  return CAMPOS.map((k) => `${ROTULOS[k]}\n${s[k]}`).join('\n\n');
}

function mensagemStatus(status: StatusSessao): string {
  switch (status) {
    case 'na_fila':
    case 'processando':
      return 'O rascunho ainda está sendo gerado. Recarregue em instantes.';
    case 'erro':
      return 'O processamento falhou — foi preciso regravar a consulta no celular.';
    default:
      return 'Rascunho indisponível.';
  }
}

type Carga =
  | { fase: 'carregando' }
  | { fase: 'pronto' }
  | { fase: 'indisponivel'; motivo: string };

export function EditorPage(): JSX.Element {
  const [sessionId, setSessionId] = useState<string | null>(
    () => sessionStorage.getItem(CHAVE_SESSAO),
  );
  const [codigo, setCodigo] = useState<string>(() => sessionStorage.getItem(CHAVE_CODIGO) ?? '');
  const [avisoPareamento, setAvisoPareamento] = useState<string | undefined>(undefined);
  const [carga, setCarga] = useState<Carga>({ fase: 'carregando' });
  const [draft, setDraft] = useState<Soap | null>(null);
  const [copiadoTudo, setCopiadoTudo] = useState(false);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);

  const ultimoSalvoRef = useRef<string>('');

  function limparSessao(aviso?: string): void {
    sessionStorage.removeItem(CHAVE_SESSAO);
    sessionStorage.removeItem(CHAVE_CODIGO);
    ultimoSalvoRef.current = '';
    setDraft(null);
    setCodigo('');
    setCopiadoTudo(false);
    setErroSalvar(null);
    setAvisoPareamento(aviso);
    setSessionId(null);
  }

  function aoParear(id: string, code: string): void {
    sessionStorage.setItem(CHAVE_SESSAO, id);
    sessionStorage.setItem(CHAVE_CODIGO, code);
    setCodigo(code);
    setAvisoPareamento(undefined);
    setSessionId(id);
  }

  // Carrega o rascunho quando há sessão.
  useEffect(() => {
    if (!sessionId) return;
    let cancelado = false;
    setCarga({ fase: 'carregando' });
    carregarSoap(sessionId)
      .then((leitura) => {
        if (cancelado) return;
        if (leitura === null) {
          limparSessao('A sessão não está mais disponível. Refaça o pareamento.');
          return;
        }
        if (leitura.status !== 'pronto' || !leitura.soap) {
          setCarga({ fase: 'indisponivel', motivo: mensagemStatus(leitura.status) });
          return;
        }
        setDraft(leitura.soap);
        ultimoSalvoRef.current = JSON.stringify(leitura.soap);
        setCopiadoTudo(leitura.notaCopiadaAt !== null);
        setCarga({ fase: 'pronto' });
      })
      .catch(() => {
        if (cancelado) return;
        setCarga({
          fase: 'indisponivel',
          motivo: 'Erro ao carregar o rascunho. Recarregue a página.',
        });
      });
    return () => {
      cancelado = true;
    };
  }, [sessionId]);

  // Autosave debounced: grava as edições 800 ms após a última tecla.
  useEffect(() => {
    if (!sessionId || !draft) return;
    const atual = JSON.stringify(draft);
    if (atual === ultimoSalvoRef.current) return;
    const t = window.setTimeout(() => {
      void (async () => {
        try {
          const ok = await salvarSoap(sessionId, draft);
          if (ok) {
            ultimoSalvoRef.current = atual;
            setErroSalvar(null);
          } else {
            limparSessao('A sessão não está mais disponível. Refaça o pareamento.');
          }
        } catch {
          setErroSalvar(
            'Não foi possível salvar. Verifique a conexão — suas edições continuam na tela.',
          );
        }
      })();
    }, 800);
    return () => window.clearTimeout(t);
  }, [draft, sessionId]);

  function atualizarCampo(campo: CampoSoap, valor: string): void {
    setDraft((d) => (d ? { ...d, [campo]: valor } : d));
  }

  async function copiarTudo(): Promise<void> {
    if (!sessionId || !draft) return;
    // Flush do PATCH ANTES de marcar copiada: o diff do piloto (soap_ia -> soap) é
    // calculado no servidor a partir de `soap` no instante do POST /copiada.
    try {
      await salvarSoap(sessionId, draft);
      ultimoSalvoRef.current = JSON.stringify(draft);
    } catch {
      /* segue: a cópia local ainda é útil ao médico mesmo se o save falhar */
    }
    await copiar(textoCompleto(draft));
    try {
      await marcarCopiada(sessionId);
    } catch {
      /* a métrica é best-effort; não travar a cópia do médico */
    }
    setCopiadoTudo(true);
  }

  if (!sessionId) {
    return avisoPareamento ? (
      <Pareamento onPareado={aoParear} aviso={avisoPareamento} />
    ) : (
      <Pareamento onPareado={aoParear} />
    );
  }

  if (carga.fase !== 'pronto' || !draft) {
    return (
      <Centralizado>
        {carga.fase === 'carregando' ? (
          <div style={{ color: cor.muted }}>Carregando rascunho…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
            <div style={{ color: cor.texto, maxWidth: 360, textAlign: 'center' }}>
              {carga.fase === 'indisponivel' ? carga.motivo : ''}
            </div>
            <button
              type="button"
              onClick={() => limparSessao()}
              style={{ ...btnVoltar }}
            >
              Voltar ao pareamento
            </button>
          </div>
        )}
      </Centralizado>
    );
  }

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        background: cor.bg,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Cabecalho
        codigo={codigo}
        onEncerrar={() => limparSessao()}
        onCopiarTudo={copiarTudo}
        copiadoTudo={copiadoTudo}
      />

      {erroSalvar && (
        <div
          style={{
            padding: '6px 24px',
            fontSize: 12.5,
            color: cor.ambarTexto,
            background: cor.ambarBg,
            borderBottom: `1px solid ${cor.ambarBorda}`,
          }}
        >
          {erroSalvar}
        </div>
      )}

      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 340px', minHeight: 0 }}>
        <div
          style={{
            padding: '18px 22px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            overflowY: 'auto',
            borderRight: `1px solid ${cor.linha}`,
          }}
        >
          <CampoSoapView
            letra="S"
            titulo="Subjetivo"
            valor={draft.subjetivo}
            onChange={(v) => atualizarCampo('subjetivo', v)}
          />
          <CampoSoapView
            letra="O"
            titulo="Objetivo"
            valor={draft.objetivo}
            onChange={(v) => atualizarCampo('objetivo', v)}
          />
          <CampoAvaliacao valor={draft.avaliacao} onChange={(v) => atualizarCampo('avaliacao', v)} />
          <CampoSoapView
            letra="P"
            titulo="Plano"
            valor={draft.plano}
            onChange={(v) => atualizarCampo('plano', v)}
          />
        </div>

        <PainelLateral soap={draft} />
      </div>
    </div>
  );
}

function Centralizado({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        background: cor.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: fonte.corpo,
      }}
    >
      {children}
    </div>
  );
}

const btnVoltar = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  fontFamily: fonte.condensada,
  fontWeight: 600,
  fontSize: 14,
  color: cor.texto,
  background: 'transparent',
  border: `1px solid ${cor.linha}`,
  borderRadius: 0,
  padding: '8px 14px',
} as const;
